"use client";

import Image from '@/components/atoms/image';
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import styles from './HeroBanner.module.css';
import { PaginationCard } from './PaginationCard';
import { IHeroSliderBlock } from '@/page-builder/fragments/HeroSliderBlock/types';

export default function HeroBanner({ slides }: IHeroSliderBlock) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  useEffect(() => {
    const timer = setTimeout(nextSlide, 5000);
    return () => clearTimeout(timer);
  }, [currentSlide]);

  return (
    <section className={`${slides?.length > 1 ? 'bg-primary-100' : ''}  flex flex-col mb-32 md:[44px] xl:mb-64  xl:bg-transparent`}>
      <div
        className={`${styles.hero}  relative w-full h-[450px] md:h-[480px] xl:h-[650px] overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-black/20 before:z-10`}
      >
        {slides.map((slide, index) => (
          <motion.div
            key={index}
            className='absolute top-[-100px] right-[-400px] xl:right-[0] w-full min-w-[1767px] h-[1178px]'
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentSlide ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <Image
              fill
              src={slide?.image?.url || slide?.image?.thumbnailURL || ''}
              alt={slide?.image?.alt || slide?.title}
              className='object-cover [object-position:50%_20%] !h-auto'
              priority={index === 0}
              quality={75}
              sizes="(max-width: 768px) 100vw, 1920px" 
              fetchPriority='high'
              loading="eager"
            />
          </motion.div>
        ))}

        <div className='relative container h-full mx-auto flex items-center z-20'>
          <div className="relative px-4 pb-[32px] xl:pb-[120px]">
            <div className="flex flex-col max-w-[592px] gap-6 text-white">
              {slides.map((slide, index) =>
                index === currentSlide && (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                    className="flex flex-col gap-[24px] max-w-[215px] md:max-w-[388px] xl:max-w-[592px]"
                  >
                    <h2 className="text-xl-bold md:text-2xl-bold xl:text-3xl-bold line-clamp-3">
                      {slide.title}
                    </h2>
                    <p className="text-m-regular xl:text-l-regular text-white line-clamp-6">
                      {slide.description}
                    </p>
                  </motion.div>
                )
              )}
            </div>
          </div>

          {slides?.length > 1 && <div
            className={`${styles.paginationCard} bg-[#F1F6F3] w-[580px] h-[200px] absolute right-0 bottom-0 xl:block hidden z-20`}
          >
            <PaginationCard
              slides={slides}
              currentSlide={currentSlide}
              title={slides[currentSlide].slideCaption}
              onNext={nextSlide}
              onPrev={prevSlide}
            />
          </div>
          }
        </div>
      </div>

      {slides?.length > 1 && <div className='container mx-auto xl:hidden py-[16px]'>
        <PaginationCard
          slides={slides}
          currentSlide={currentSlide}
          title={slides[currentSlide].slideCaption}
          onNext={nextSlide}
          onPrev={prevSlide}
        />
      </div> }
    </section>
  );
}